import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api, setAuthToken } from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/e8a02fd5-3c12-4859-8d0e-46bed620e093/images/1193b7a041639f784b7a40c1e423439a99456520d92788df70354c3419c35964.png";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for OAuth errors in URL
    const error = searchParams.get('error');
    if (error) {
      if (error === 'redirect_uri_not_registered') {
        const hint = searchParams.get('hint');
        toast.error(
          <div className="space-y-1">
            <p className="font-medium">Google Sign-in Configuration Error</p>
            <p className="text-sm">The redirect URI needs to be added to Google Cloud Console.</p>
            {hint && <p className="text-xs text-stone-500">{decodeURIComponent(hint)}</p>}
          </div>,
          { duration: 10000 }
        );
      } else if (error === 'oauth_failed') {
        toast.error('Google sign-in failed. Please try again or use email login.');
      } else {
        toast.error(`Sign-in error: ${error}`);
      }
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;

      // Persist token for Authorization header fallback (cross-site cookie safe)
      if (token) setAuthToken(token);

      toast.success(`Welcome back, ${userData.name}!`);
      navigate(`/${userData.role}`, { replace: true, state: { user: userData } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      // Fetch OAuth config from backend at runtime to get the latest client ID
      const configResponse = await api.get('/auth/config');
      const { google_client_id, google_enabled, redirect_uri } = configResponse.data;
      
      if (!google_enabled || !google_client_id) {
        toast.error('Google Sign-in is not configured');
        setGoogleLoading(false);
        return;
      }
      
      // Build the Google OAuth URL with the fetched client ID and redirect URI from backend
      const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      googleAuthUrl.searchParams.set('client_id', google_client_id);
      googleAuthUrl.searchParams.set('redirect_uri', redirect_uri);
      googleAuthUrl.searchParams.set('response_type', 'code');
      googleAuthUrl.searchParams.set('scope', 'openid email profile');
      googleAuthUrl.searchParams.set('access_type', 'offline');
      
      // Redirect to Google OAuth
      window.location.href = googleAuthUrl.toString();
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      toast.error('Failed to start Google Sign-in. Please try again.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex transition-colors duration-300">
      {/* Left side - Image (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1627141234469-24711efb373c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvbWUlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYXJjaGl0ZWN0fGVufDB8fHx8MTc2OTQ4NTU0M3ww&ixlib=rb-4.1.0&q=85"
          alt="Construction site"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/40 to-transparent"></div>
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <h2 className="text-4xl font-heading font-bold mb-4">Build Your Dream Home</h2>
          <p className="text-lg text-slate-200">Track every milestone of your construction journey</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo & Title */}
          <div className="text-center animate-fade-in">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img src={LOGO_URL} alt="MyHomeEngineer" className="w-12 h-12 object-contain" />
              <span className="text-2xl font-heading font-bold text-foreground">MyHomeEngineer</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-2">Welcome Back</h1>
            <p className="text-muted-foreground">Sign in to continue to your project dashboard</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div className="space-y-2 animate-fade-in stagger-1">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-card border-border focus:ring-2 focus:ring-primary/20 rounded-xl touch-target transition-all"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2 animate-fade-in stagger-2">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-12 h-12 bg-card border-border focus:ring-2 focus:ring-primary/20 rounded-xl touch-target transition-all"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-lg shadow-primary/20 transition-all duration-200 active:scale-95 touch-target animate-fade-in stagger-3"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2"></div>
                  Signing in...
                </span>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative animate-fade-in stagger-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full h-12 border-border hover:bg-accent/10 rounded-full font-medium transition-all duration-200 active:scale-95 touch-target animate-fade-in stagger-5"
            data-testid="google-login-button"
          >
            {googleLoading ? (
              <span className="flex items-center">
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                Connecting...
              </span>
            ) : (
              <>
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </>
            )}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary hover:text-primary/80 font-medium transition-colors">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;